/**
 * MatchSuggestionCard — Shows a pending match suggestion (Flow A)
 *
 * Displayed when a Pool 2 verdict has a matching ART_INCOME transaction.
 * User can link them or dismiss.
 */

import { T } from '@/styles/tokens';
import type { MatchSuggestion } from '@/hooks/useMatchSuggestions';

interface MatchSuggestionCardProps {
  suggestion: MatchSuggestion;
  onLink: () => void;
  onDismiss: () => void;
}

export function MatchSuggestionCard({ suggestion, onLink, onDismiss }: MatchSuggestionCardProps) {
  const verdictDollars = Math.round(suggestion.verdictSoldPrice / 100);
  const txnDollars = Math.round(suggestion.txnAmount / 100);

  return (
    <div style={{
      background: T.white,
      borderRadius: 12,
      padding: 16,
      border: `1px solid ${T.border}`,
    }}>
      {/* Label */}
      <div style={{
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: T.primary,
        marginBottom: 12,
      }}>
        Match found
      </div>

      {/* Context */}
      <div style={{ fontSize: 14, color: T.text, fontFamily: T.fontFamily, marginBottom: 4 }}>
        Your sale of &ldquo;{suggestion.verdictTitle}&rdquo;
      </div>
      <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.fontFamily, marginBottom: 12 }}>
        ${verdictDollars.toLocaleString()} · {suggestion.txnDate ? new Date(suggestion.txnDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
      </div>

      <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.fontFamily, marginBottom: 8 }}>
        matches a deposit:
      </div>

      {/* Deposit card */}
      <div style={{
        background: T.cream,
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 500, color: T.text, fontFamily: T.fontFamily }}>
              ${txnDollars.toLocaleString()} · {suggestion.txnMerchant || 'Unknown'}
            </div>
            <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.fontFamily, marginTop: 2 }}>
              {suggestion.txnDate} · {suggestion.matchPercent}% of sale price
            </div>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onLink}
          style={{
            flex: 1,
            height: 44,
            borderRadius: 12,
            border: 'none',
            background: T.primary,
            color: T.white,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: T.fontFamily,
            cursor: 'pointer',
          }}
        >
          Link These
        </button>
        <button
          onClick={onDismiss}
          style={{
            flex: 1,
            height: 44,
            borderRadius: 12,
            border: `1.5px solid ${T.primary}`,
            background: 'transparent',
            color: T.primary,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: T.fontFamily,
            cursor: 'pointer',
          }}
        >
          Not a Match
        </button>
      </div>
    </div>
  );
}

export default MatchSuggestionCard;
